import React from 'react';
import { Select } from 'antd';
import type { InternalMedicineData } from '../../hooks/useConsultationForm';

interface Props {
  internalMedicineData: InternalMedicineData;
  setInternalMedicineField: (section: keyof InternalMedicineData, field: string, value: any) => void;
  patientSex: 'male' | 'female' | null;
  patientAge: number | null;
}

const RISK_TABLE_OPTIONS = [
  { value: 'Framingham', label: 'Framingham' },
  { value: 'SCORE', label: 'SCORE' },
  { value: 'RD Local', label: 'RD Local' },
];

const RISK_FACTOR_OPTIONS = [
  { value: 'age', label: 'Edad (>55 hombre / >65 mujer)' },
  { value: 'smoking', label: 'Tabaquismo' },
  { value: 'hypertension', label: 'HTA' },
  { value: 'diabetes', label: 'Diabetes' },
  { value: 'low_hdl', label: 'HDL bajo' },
  { value: 'obesity', label: 'Obesidad' },
  { value: 'family_history', label: 'Antecedentes familiares' },
];

const INTERPRETATION_OPTIONS = [
  { value: 'Bajo (<10%)', label: 'Bajo (<10%)' },
  { value: 'Moderado (10-20%)', label: 'Moderado (10-20%)' },
  { value: 'Alto (>20%)', label: 'Alto (>20%)' },
  { value: 'Muy Alto (>30%)', label: 'Muy Alto (>30%)' },
];

const CardiovascularRisk: React.FC<Props> = ({ internalMedicineData, setInternalMedicineField, patientSex, patientAge }) => {
  const cv = internalMedicineData.cv_risk;
  const set = (field: string, value: any) => setInternalMedicineField('cv_risk', field, value);

  const isFactorChecked = (value: string): boolean => cv.risk_factors.includes(value);

  const handleFactorToggle = (value: string, checked: boolean) => {
    const updated = checked
      ? [...cv.risk_factors, value]
      : cv.risk_factors.filter((f) => f !== value);
    set('risk_factors', updated);
  };

  const getRiskColor = (percent: number | null): string => {
    if (percent === null) return '#6b7280';
    if (percent < 10) return '#10b981';
    if (percent < 20) return '#f59e0b';
    if (percent < 30) return '#f97316';
    return '#dc2626';
  };

  const getRiskLabel = (percent: number | null): string => {
    if (percent === null) return '';
    if (percent < 10) return 'Bajo';
    if (percent < 20) return 'Moderado';
    if (percent < 30) return 'Alto';
    return 'Muy Alto';
  };

  const riskColor = getRiskColor(cv.calculated_risk_percent);

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">Riesgo Cardiovascular</h5>
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Tabla de Riesgo</label>
              <Select
                className="w-100"
                value={cv.risk_table || undefined}
                onChange={(v) => set('risk_table', v)}
                placeholder="Seleccionar..."
                options={RISK_TABLE_OPTIONS}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <h6 className="text-muted fw-semibold">Factores de Riesgo</h6>
            <div className="d-flex flex-wrap gap-3">
              {RISK_FACTOR_OPTIONS.map((factor) => (
                <div className="form-check" key={factor.value}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`cvrf-${factor.value}`}
                    checked={isFactorChecked(factor.value)}
                    onChange={(e) => handleFactorToggle(factor.value, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`cvrf-${factor.value}`}>{factor.label}</label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            {cv.data_complete && cv.calculated_risk_percent !== null ? (
              <div
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#fff',
                  background: riskColor,
                }}
              >
                Riesgo a 10 años: {cv.calculated_risk_percent}% — {getRiskLabel(cv.calculated_risk_percent)}
              </div>
            ) : (
              <div className="alert alert-info py-2 mb-0">
                <i className="fa fa-info-circle me-1"></i>
                Complete los datos de perfil lipídico y factores de riesgo para calcular el riesgo cardiovascular.
                {cv.missing_factors.length > 0 && (
                  <span className="d-block small mt-1">
                    Factores faltantes: {cv.missing_factors.join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Interpretación del Riesgo</label>
              <Select
                className="w-100"
                value={cv.interpretation || undefined}
                onChange={(v) => set('interpretation', v)}
                placeholder="Seleccionar..."
                options={INTERPRETATION_OPTIONS}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardiovascularRisk;
